/**
 * Revoke the demo-viewer pass during sign-out.
 *
 * The gym picker includes CheerVue Demo Gym whenever the signed-in user still
 * holds a `demo_org_viewers` grant. Exit Demo removes that grant; Sign out did
 * not, so logging out while browsing the demo gym left it on the welcome
 * screen at the next login.
 *
 * Call this WHILE the session token is still valid, before supabase.auth.signOut().
 * Fail-open: a network error must not block sign-out.
 */

import { SHARED_DEMO_ORG_ID, DEMO_REAL_ORG_STORAGE_KEY } from '@/utils/demo/demoConfig';
import { clearAllDemoTeamBuilderStates } from '@/services/teamBuilder/demoStorage';
import { createLogger } from '@/utils/logger/devLogger';
import {
  rememberOrganization,
  forgetRememberedOrganization,
} from '@/utils/organizations/rememberedOrganization';

const logger = createLogger('revokeDemoAccessOnSignOut');

export const SIGN_OUT_EXIT_TIMEOUT_MS = 5000;
const ORG_SELECTION_COOKIE = 'currentOrganizationId';

export function restoreRememberedGymAfterDemo(): void {
  if (typeof window === 'undefined') return;

  const savedRealOrgId = localStorage.getItem(DEMO_REAL_ORG_STORAGE_KEY);
  const currentOrgId = localStorage.getItem('currentOrganizationId');

  if (savedRealOrgId && currentOrgId === SHARED_DEMO_ORG_ID) {
    persistOrgSelection(savedRealOrgId);
  } else if (currentOrgId === SHARED_DEMO_ORG_ID) {
    persistOrgSelection(null);
  }

  localStorage.removeItem(DEMO_REAL_ORG_STORAGE_KEY);
  clearAllDemoTeamBuilderStates();
}

function persistOrgSelection(organizationId: string | null): void {
  if (!organizationId) {
    forgetRememberedOrganization();
    document.cookie = `${ORG_SELECTION_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
    return;
  }
  rememberOrganization(organizationId, null);
  document.cookie = `${ORG_SELECTION_COOKIE}=${encodeURIComponent(organizationId)}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`;
}

export async function revokeDemoAccessOnSignOut(
  accessToken: string | null | undefined,
): Promise<void> {
  try {
    if (accessToken) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), SIGN_OUT_EXIT_TIMEOUT_MS);
      try {
        await fetch('/api/demo/exit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ demoOrgId: SHARED_DEMO_ORG_ID }),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeout);
      }
    }
  } catch (err) {
    logger.warn('Could not revoke demo access during sign-out; continuing with sign-out', err);
  } finally {
    restoreRememberedGymAfterDemo();
  }
}
